import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import toast from 'react-hot-toast';
import {
    User,
    signInWithPopup,
    GoogleAuthProvider,
    signInWithEmailAndPassword,
    signOut as firebaseSignOut,
    onAuthStateChanged,
    Auth
} from 'firebase/auth';
import { Loading } from '@nextui-org/react';
import { initializeFirebase, auth as globalAuth } from '../config/firebase';
import { getFirebaseConfig } from '../services/configService';
import ErrorPage from '../pages/Error';

interface AuthContextType {
    user: User | null;
    loading: boolean;
    environment: string;
    config: any | null;
    loginWithGoogle: () => Promise<void>;
    loginWithEmail: (email: string, pass: string) => Promise<void>;
    logout: () => Promise<void>;
    switchEnvironment: (env: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [environment, setEnvironment] = useState('Development');
    const [authInstance, setAuthInstance] = useState<Auth | null>(null);
    const [error, setError] = useState<{ title: string, description: string, code?: number } | null>(null);
    const [config, setConfig] = useState<any | null>(null);

    // Initialize Firebase when environment changes
    useEffect(() => {
        const init = async () => {
            setLoading(true);
            setError(null);
            try {
                const fetchedConfig = await getFirebaseConfig(environment);
                setConfig(fetchedConfig);
                const { auth } = await initializeFirebase(fetchedConfig, environment);
                setAuthInstance(auth);
            } catch (e: any) {
                toast.error("An error setting up the application occurred.", { duration: 5000 });
                console.error("Failed to initialize Firebase", e);
                if (e.status === 403) {
                    setError({ title: "Access Denied", description: "You do not have permission to access this environment.", code: 403 });
                } else if (e.status === 500) {
                    setError({ title: "System Error", description: "An unexpected error occurred while loading the configuration.", code: 500 });
                } else {
                    setError({ title: "Initialization Failed", description: "Failed to initialize application configuration.", code: 500 });
                }
                setLoading(false);
            }
        };
        init();
    }, [environment]);

    // Subscribe to Auth changes when auth instance is ready
    useEffect(() => {
        if (!authInstance) return;

        const unsubscribe = onAuthStateChanged(authInstance, (currentUser) => {
            setUser(currentUser);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [authInstance]);

    const switchEnvironment = async (env: string) => {
        // If we are in an error state, we allow switching even if it's the same env (to retry),
        // but here we just update state. The effect handles it.
        if (env === environment && !error) return;
        setEnvironment(env);
    };

    const loginWithGoogle = async () => {
        const currentAuth = authInstance || globalAuth;
        if (!currentAuth) {
            console.error("Auth not initialized");
            return;
        }
        const provider = new GoogleAuthProvider();
        try {
            await signInWithPopup(currentAuth, provider);
        } catch (error) {
            console.error("Error signing in with Google", error);
            throw error;
        }
    };

    const loginWithEmail = async (email: string, pass: string) => {
        const currentAuth = authInstance || globalAuth;
        if (!currentAuth) {
            console.error("Auth not initialized");
            return;
        }
        try {
            await signInWithEmailAndPassword(currentAuth, email, pass);
        } catch (error) {
            console.error("Error signing in with Email", error);
            throw error;
        }
    };

    const logout = async () => {
        const currentAuth = authInstance || globalAuth;
        if (!currentAuth) {
            console.error("Auth not initialized");
            return;
        }
        try {
            await firebaseSignOut(currentAuth);
        } catch (error) {
            console.error("Error signing out", error);
            throw error;
        }
    };

    if (loading) {
        return (
            <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '1rem' }}>
                <Loading size="xl" />
                <p style={{ fontFamily: 'system-ui', color: '#666' }}>Initializing {environment} Environment...</p>
            </div>
        );
    }

    if (error) {
        return <ErrorPage
            title={error.title}
            description={error.description}
            code={error.code}
            onRetry={() => {
                window.location.reload()
            }}
        />;
    }

    return (
        <AuthContext.Provider value={{ user, loading, environment, config, loginWithGoogle, loginWithEmail, logout, switchEnvironment }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
